/**
 * /qui-sommes-nous — À propos de SmartLawyer
 */

import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PublicHeader, PublicFooter } from '@/components/jurisprudence';
import { Breadcrumb } from '@/components/jurisprudence/Breadcrumb';

const SITE_URL = 'https://smartlawyer.ai';

const TEAM = [
  {
    name:   'Pierre-Louis',
    id:     'pierre_louis', // textes : about.page.team.members.pierre_louis.role / .desc
    emoji:  '⚖️',
    color:  'bg-blue-50 text-blue-600 dark:bg-blue-950/30 dark:text-blue-400',
  },
  {
    name:   'Jérémie',
    id:     'jeremie', // textes : about.page.team.members.jeremie.role / .desc
    emoji:  '🛠️',
    color:  'bg-violet-50 text-violet-600 dark:bg-violet-950/30 dark:text-violet-400',
  },
  {
    name:   'Raphaël',
    id:     'raphael', // textes : about.page.team.members.raphael.role / .desc
    emoji:  '🎯',
    color:  'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/30 dark:text-emerald-400',
  },
  {
    name:   'Mathieu',
    id:     'mathieu', // textes : about.page.team.members.mathieu.role / .desc
    emoji:  '🤝',
    color:  'bg-amber-50 text-amber-600 dark:bg-amber-950/30 dark:text-amber-400',
  },
];

// Textes : about.page.values.items.<id>.title / .desc
const VALUES = [
  { id: 'sovereignty',  icon: '🇪🇺' },
  { id: 'no_ads',       icon: '🚫' },
  { id: 'transparent',  icon: '🔍' },
  { id: 'open',         icon: '🔗' },
];

export function QuiSommesNous() {
  const { t } = useTranslation();
  return (
    <>
      <Helmet>
        <title>{t('about.page.meta_title')}</title>
        <meta name="description"
          content={t('about.page.meta_description')} />
        <link rel="canonical" href={`${SITE_URL}/qui-sommes-nous`} />
      </Helmet>

      <div className="min-h-screen bg-background flex flex-col">
        <PublicHeader />

        <Breadcrumb items={[
          { label: t('menu.home'),       to: '/' },
          { label: t('about.page.breadcrumb') },
        ]} />

        <main className="flex-1">

          {/* Hero */}
          <section className="py-20 border-b">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
              <div className="inline-flex items-center gap-2 text-xs font-medium text-primary bg-primary/10 border border-primary/20 px-3 py-1.5 rounded-full mb-6">
                {t('about.eyebrow')}
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold leading-tight tracking-tight mb-6">
                {t('about.page.hero_title_start')}{' '}
                <span className="text-primary">{t('about.page.hero_title_highlight')}</span>
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
                {t('about.page.hero_desc')}
              </p>
            </div>
          </section>

          {/* Mission */}
          <section className="py-16 border-b">
            <div className="max-w-3xl mx-auto px-4 sm:px-6">
              <h2 className="text-2xl font-bold mb-6">{t('about.page.mission.title')}</h2>
              <div className="space-y-4 text-muted-foreground leading-relaxed">
                <p>
                  {t('about.page.mission.p1')}
                </p>
                <p>
                  {t('about.page.mission.p2')}
                </p>
                <p>
                  {t('about.page.mission.p3')}
                </p>
              </div>
            </div>
          </section>

          {/* Équipe */}
          <section className="py-16 border-b">
            <div className="max-w-4xl mx-auto px-4 sm:px-6">
              <h2 className="text-2xl font-bold mb-2">{t('about.page.team.title')}</h2>
              <p className="text-muted-foreground mb-10">
                {t('about.page.team.desc')}
              </p>
              <div className="grid sm:grid-cols-2 gap-6">
                {TEAM.map(({ name, id, emoji, color }) => (
                  <div key={name} className="rounded-xl border bg-card p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`w-11 h-11 rounded-xl flex items-center justify-center text-xl ${color}`}>
                        {emoji}
                      </div>
                      <div>
                        <p className="font-semibold">{name}</p>
                        <p className="text-xs text-muted-foreground">{t(`about.page.team.members.${id}.role`)}</p>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">{t(`about.page.team.members.${id}.desc`)}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Valeurs */}
          <section className="py-16 border-b">
            <div className="max-w-4xl mx-auto px-4 sm:px-6">
              <h2 className="text-2xl font-bold mb-2">{t('about.page.values.title')}</h2>
              <p className="text-muted-foreground mb-10">{t('about.page.values.desc')}</p>
              <div className="grid sm:grid-cols-2 gap-5">
                {VALUES.map(({ id, icon }) => (
                  <div key={id} className="rounded-xl border bg-card p-5">
                    <div className="text-2xl mb-3">{icon}</div>
                    <p className="font-semibold mb-1.5">{t(`about.page.values.items.${id}.title`)}</p>
                    <p className="text-sm text-muted-foreground leading-relaxed">{t(`about.page.values.items.${id}.desc`)}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="py-16">
            <div className="max-w-2xl mx-auto px-4 sm:px-6 text-center">
              <h2 className="text-2xl font-bold mb-4">{t('about.page.cta.title')}</h2>
              <p className="text-muted-foreground mb-8">
                {t('about.page.cta.desc')}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/contact"
                  className="px-6 py-3 rounded-xl bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors">
                  {t('about.page.cta.contact')}
                </Link>
                <Link to="/"
                  className="px-6 py-3 rounded-xl border-2 hover:bg-accent transition-colors text-sm font-medium">
                  {t('about.page.cta.try')}
                </Link>
              </div>
            </div>
          </section>

        </main>

        <PublicFooter />
      </div>
    </>
  );
}